import { FC, PropsWithChildren, ReactNode, TransitionEvent, useEffect, useRef, useState } from "react";
import classes from './openContainer.module.scss'


interface IProps {
    title: ReactNode;
    icon?: ReactNode;
    trigger?: number; // меняем, если внешний компонент обновил children и его height изменился (новые данные)
    allowOverflow?: boolean;
    rotateChevron?: boolean;
    defaultOpen?: boolean;
}

export const OpenContainer: FC<IProps & PropsWithChildren> = ({title, icon, trigger, allowOverflow = false, rotateChevron = true, defaultOpen = false, children}) => {

    const contentRef = useRef<HTMLDivElement>(null)
    const contentInnerRef = useRef<HTMLDivElement>(null)
    const isHeightTransitioningRef = useRef<boolean>(false)
    const [open, setOpen] = useState<boolean>(defaultOpen)
    const [isOverflowVisible, setIsOverflowVisible] = useState<boolean>(false)

    const setHeight = () => {
        if(contentRef.current && contentInnerRef.current){
            const contentHeight = contentInnerRef.current.scrollHeight
            const currentTargetHeight = Math.round(parseFloat(contentRef.current.style.height || '0'))
            const isHeightChanged = currentTargetHeight !== contentHeight

            if(isHeightChanged){
                isHeightTransitioningRef.current = true
                setIsOverflowVisible(false)
            }

            contentRef.current.style.height = contentHeight + 'px';

            if(!isHeightChanged && !isHeightTransitioningRef.current){
                requestAnimationFrame(() => {
                    if(open && allowOverflow){
                        setIsOverflowVisible(true)
                    }
                })
            }
        }
    }

    const onOpen = () => {
        if(contentRef.current){
            const newOpen = !open;
            
            if(newOpen){
                isHeightTransitioningRef.current = true
                setHeight()
            }
            else{
                isHeightTransitioningRef.current = true
                setIsOverflowVisible(false)
                contentRef.current.style.height = '0px'
            }
    
            setOpen(newOpen)
        }
    }

    const changeHeight = () => {
        if(open && contentRef.current){
            setHeight()
        }
    }

    useEffect(() => {
        changeHeight()
    }, [trigger])

    useEffect(() => {
        if(!open || !contentInnerRef.current || typeof ResizeObserver === 'undefined'){
            return
        }

        const observer = new ResizeObserver(() => {
            setHeight()
        })

        observer.observe(contentInnerRef.current)

        return () => {
            observer.disconnect()
        }
    }, [open])

    useEffect(() => {
        if(!open){
            return
        }

        setHeight()
        const onResize = () => setHeight()
        window.addEventListener('resize', onResize)

        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [open])

    const onTransitionEnd = (event: TransitionEvent<HTMLElement>) => {
        if(event.target !== event.currentTarget || event.propertyName !== 'height'){
            return
        }

        if(open && allowOverflow){
            isHeightTransitioningRef.current = false
            setIsOverflowVisible(true)
            return
        }

        isHeightTransitioningRef.current = false
    }

    return (
        <section className={classes.container}>
            <section 
                onClick={onOpen} 
                className={classes.title}
                onMouseDown={e => e.preventDefault()}
            >
                <section className={classes.titleLabel}>
                    {icon && <span className={classes.titleIcon}>{icon}</span>}
                    {title}
                </section>
                 <svg className={open && rotateChevron ? classes.open : ''} width="19" height="11" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.23181 8.68484C7.65607 9.10505 8.34392 9.10505 8.76818 8.68484L15.6818 1.83693C16.1061 1.4167 16.1061 0.735389 15.6818 0.315166C15.2575 -0.105055 14.5697 -0.105055 14.1455 0.315166L7.99999 6.4022L1.85456 0.315166C1.4303 -0.105055 0.742449 -0.105055 0.31819 0.315166C-0.106063 0.735388 -0.106063 1.4167 0.31819 1.83693L7.23181 8.68484ZM6.91362 6.84791V7.92395H9.08636V6.84791H6.91362Z" fill="black"/>
                </svg>
            </section>
            <section 
                ref={contentRef} 
                className={classes.content + (isOverflowVisible ? ` ${classes.contentVisible}` : '')}
                onTransitionEnd={onTransitionEnd}
            >
                <section ref={contentInnerRef} className={classes.contentInner}>
                    {children}
                </section>
            </section>
        </section>
    )
}
